import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Text,
  VStack,
  Heading,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon, MinusIcon } from "@chakra-ui/icons";
import { FiArrowUpRight, FiArrowDownLeft, FiRefreshCw } from "react-icons/fi";
import { FaPlaneDeparture, FaUser } from "react-icons/fa";
import Translate from "../../LanguageTranslation/Translate";
import { useTranslation } from "../../LanguageTranslation/TranslationContext";
import axiosInstance from "../../../helpers/axiosInstance";

const BRAND = "#286328";
const dd = {
  variant: "ghost",
  fontWeight: "bold",
  px: 4,
  py: 2,
  border: "1px solid #d8d8d8",
  borderRadius: "md",
  bg: "#FFFBEF",
  _hover: { bg: "#F5F5F5" },
};

const tripTypeLabel = {
  round: "Round Trip",
  oneWay: "One Way",
  multi: "Multi-city",
};
const CABIN_OPTIONS = [
  { value: "economy", label: "Economy" },
  { value: "business", label: "Business" },
  { value: "first", label: "First" },
];
const PAX_VALUES = Array.from({ length: 9 }, (_, i) => i + 1);
const FLIGHT_ROWS = [
  { key: "short", label: "Short Flights", sub: "< 3 hours" },
  { key: "medium", label: "Medium Flights", sub: "3–6 hours" },
  { key: "long", label: "Long Flights", sub: "6+ hours" },
];

export default function FlightTripDetails({
  // lifted state props
  tripMode,
  setTripMode,
  tripType,
  setTripType,
  cabinTab,
  setCabinTab,
  passengers,
  setPassengers,
  from,
  setFrom,
  to,
  setTo,
  counts,
  setCounts,
  result,
  setResult,
  selectedTrip,
  hasTrips,
}) {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Where From?");
  const placeholderTextTo = useTranslate("Where to?");

  // API call state
  const [loading, setLoading] = useState(false);

  // swap handler
  const swap = () => {
    setFrom(to);
    setTo(from);
  };
  // multi-leg counters
  const inc = (k) => setCounts((p) => ({ ...p, [k]: p[k] + 1 }));
  const dec = (k) => setCounts((p) => ({ ...p, [k]: Math.max(0, p[k] - 1) }));

  // calculate emissions
  const calculateEmission = async () => {
    // Check if trip is selected / any trips exist
    if (!selectedTrip || selectedTrip.trim() === '') {
      alert(hasTrips
        ? 'Please select a trip before calculating emissions.'
        : 'No trips found. Please create a trip plan first, then select it before calculating emissions.'
      );
      return;
    }

    setLoading(true);
    try {
      if (tripMode === "multi") {
        const payload = {
          short_flights: counts.short,
          medium_flights: counts.medium,
          long_flights: counts.long,
          travel_class: cabinTab,
        };
        const { data } = await axiosInstance.post(
          "/co2-emissions/calculate/multiple-flights",
          payload
        );
        // Normalize for consumers expecting emissions_kg_co2e
        setResult({ ...data, emissions_kg_co2e: data.total_emissions_kg_co2e });
      } else {
        const payload = {
          departure_location: from,
          destination_location: to,
          is_return_trip: tripType === "round",
          travel_class: cabinTab,
          number_of_passengers: passengers,
        };
        const { data } = await axiosInstance.post(
          "/co2-emissions/calculate/flight",
          payload
        );
        setResult(data);
      }
    } catch (err) {
      console.error("Error calculating flight emission:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* One vs Multi selector */}
      <RadioGroup value={tripMode} onChange={setTripMode} mb={8}>
        <HStack spacing={6}>
          <Radio value="one" colorScheme="green">
            <Translate text="One Trip" />
          </Radio>
          <Radio value="multi" colorScheme="green">
            <Translate text="Multiple Trips" />
          </Radio>
        </HStack>
      </RadioGroup>

      {/* ============ ONE TRIP ============ */}
      {tripMode === "one" && (
        <>
          <HStack spacing={4} mb={8} flexWrap="wrap">
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} {...dd}>
                <Translate text={tripTypeLabel[tripType]} />
              </MenuButton>
              <MenuList>
                {Object.entries(tripTypeLabel).map(([k, l]) => (
                  <MenuItem key={k} onClick={() => setTripType(k)}>
                    <Translate text={l} />
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} {...dd}>
                <HStack>
                  <Icon as={FaUser} boxSize={3} />
                  <Text mb={0}>{passengers}</Text>
                </HStack>
              </MenuButton>
              <MenuList h="250px" overflow="auto">
                {PAX_VALUES.map((n) => (
                  <MenuItem key={n} onClick={() => setPassengers(n)}>
                    <HStack>
                      <Icon as={FaUser} boxSize={3} />
                      <Text mb={0}>{n}</Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} {...dd}>
                <Translate
                  text={CABIN_OPTIONS.find((o) => o.value === cabinTab)?.label}
                />
              </MenuButton>
              <MenuList>
                {CABIN_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} onClick={() => setCabinTab(value)}>
                    <Translate text={label} />
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>

          <HStack w="full" mb={8} spacing={4}>
            <InputGroup flex="1">
              <InputLeftElement
                children={<FiArrowUpRight />}
                color="gray.500"
                py={8}
              />
              <Input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder={placeholderText}
                border="1px solid #d8d8d8"
                _focus={{ borderColor: BRAND }}
                borderRadius="md"
                py={8}
              />
            </InputGroup>

            <IconButton
              aria-label="swap"
              icon={<FiRefreshCw />}
              variant="ghost"
              onClick={swap}
              color="gray.600"
              _hover={{ color: BRAND }}
            />

            <InputGroup flex="1">
              <InputLeftElement
                children={<FiArrowDownLeft />}
                color="gray.500"
                py={8}
              />
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={placeholderTextTo}
                border="1px solid #d8d8d8"
                _focus={{ borderColor: BRAND }}
                borderRadius="md"
                py={8}
              />
            </InputGroup>
          </HStack>
        </>
      )}

      {/* ============ MULTI TRIP ============ */}
      {tripMode === "multi" && (
        <>
          <Heading size="sm" mb={6}>
            <Translate text="How many flights would you like to offset?" />
          </Heading>

          <Menu>
            <MenuButton as={Button} mb={6} rightIcon={<ChevronDownIcon />} {...dd}>
              <Translate
                text={CABIN_OPTIONS.find((o) => o.value === cabinTab)?.label}
              />
            </MenuButton>
            <MenuList>
              {CABIN_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} onClick={() => setCabinTab(value)}>
                  <Translate text={label} />
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          <VStack align="stretch" spacing={6} mb={8}>
            {FLIGHT_ROWS.map(({ key, label, sub }) => (
              <Flex key={key} align="center">
                <Icon as={FaPlaneDeparture} mr={4} />
                <Box flex="1">
                  <Text fontWeight="bold" mb={0}>
                    <Translate text={label} />
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={0}>
                    <Translate text={sub} />
                  </Text>
                </Box>
                <IconButton
                  aria-label="minus"
                  icon={<MinusIcon />}
                  size="sm"
                  variant="ghost"
                  border="1px solid"
                  borderColor={BRAND}
                  color={BRAND}
                  isDisabled={counts[key] === 0}
                  onClick={() => dec(key)}
                  _hover={{ bg: "transparent" }}
                />
                <Text w="32px" textAlign="center" mb={0}>
                  {counts[key]}
                </Text>
                <IconButton
                  aria-label="plus"
                  icon={<AddIcon />}
                  size="sm"
                  variant="ghost"
                  border="1px solid"
                  borderColor={BRAND}
                  color={BRAND}
                  onClick={() => inc(key)}
                  _hover={{ bg: "transparent" }}
                />
              </Flex>
            ))}
          </VStack>
        </>
      )}

      <Divider mb={8} />

      <Button
        w="full"
        bg={BRAND}
        color="white"
        _hover={{ bg: BRAND }}
        borderRadius="lg"
        py={6}
        onClick={calculateEmission}
        isLoading={loading}
      >
        <Translate text="Calculate" />
      </Button>
    </>
  );
}
